import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { useTheme } from "../context/ThemeContext";
import { X, AlertCircle } from "lucide-react";
import ConfirmModal from "./ConfirmModal";
import Header from "./Header";
import SEO from "./SEO";
import "./Pricing.css";
import Footer from "./Footer";
import { fireCheckoutIntent } from "../utils/checkoutAnalytics";
import {
  shouldBlockSubscriptionAccess,
  isInPaymentGracePeriod,
} from "../utils/subscriptionAccess";

export default function Pricing() {
  const navigate = useNavigate();
  const location = useLocation();
  const autoSwitchHandledRef = useRef(false);
  const promptCancelTrialHandledRef = useRef(false);
  const { isAuthenticated, user, verifyToken } = useAuth();
  const { showSuccess, showError, showInfo, showWarning } = useNotification();
  const { darkMode } = useTheme();
  const [subscription, setSubscription] = useState(null);
  // Change to object to support multiple expanded items independently
  const [expanded, setExpanded] = useState({});
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmSwitchOpen, setConfirmSwitchOpen] = useState(false);
  const [pendingSwitchPlan, setPendingSwitchPlan] = useState(null);
  const [confirmUpgradeOpen, setConfirmUpgradeOpen] = useState(false);
  const [confirmCancelTrialOpen, setConfirmCancelTrialOpen] = useState(false);

  const plans = [
    {
      name: "Essentials",
      price: "$19.95/month",
      amount: 19.95,
      actions: "50 AI Actions",
      features: [
        "Full access to the LawFusion Research, Intelligence & Debate Engine",
        "50 AI Actions per month",
        "Up to 25 saved projects securely stored",
        "Purchase additional AI Actions at $10 per 25 AI Actions",
      ],
      highlight: true,
    },
    {
      name: "Professional",
      price: "$29.95/month",
      amount: 29.95,
      actions: "100 AI Actions",
      features: [
        "Full access to the LawFusion Research, Intelligence & Debate Engine",
        "Designed for attorneys who want more room for research, drafting, and argument testing",
        "100 AI Actions per month",
        "Up to 50 saved projects securely stored",
        "Purchase additional AI Actions at $10 per 25 AI Actions",
      ],
      highlight: false,
    },
  ];

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscriptionStatus();
    }
  }, [isAuthenticated]);

  const fetchSubscriptionStatus = async () => {
    try {
      const resp = await axios.get(`${API_URL}/payments/subscription-status`, {
        withCredentials: true,
      });
      setSubscription(resp.data);
    } catch (err) {
      console.error("Error fetching subscription status:", err);
    }
  };

  const toggleExpand = (index) => {
    setExpanded((prev) => ({
      ...prev,
      [index]: !prev[index], // Toggle specific index independently
    }));
  };

  const nf = new Intl.NumberFormat();
  const rolloverLine = (actions) => {
    const n = parseInt(String(actions).replace(/[^0-9]/g, ""), 10);
    if (!n || isNaN(n)) return null;
    const max = n * 2;
    return (
      <p className="rollover-text">
        AI Actions roll over each month — you can bank up to 2× your plan total
        (e.g., {nf.format(n)} → {nf.format(max)} max).
      </p>
    );
  };

  const startCheckout = async (plan) => {
    if (!isAuthenticated) {
      // Remember the selected plan so we can proceed to checkout after signup
      localStorage.setItem("pendingPlan", JSON.stringify({
        name: plan.name,
        price: plan.amount,
      }));
      navigate("/signup", { state: { from: { pathname: "/pricing" } } });
      return;
    }

    const currentPlan = subscription?.subscriptionPlan?.toLowerCase();
    const nextPlan = plan?.name?.toLowerCase();
    const isTrialSwitchBetweenPaidPlans =
      subscription?.subscriptionStatus === "trialing" &&
      (currentPlan === "essentials" || currentPlan === "professional") &&
      (nextPlan === "essentials" || nextPlan === "professional") &&
      currentPlan !== nextPlan;

    if (isTrialSwitchBetweenPaidPlans) {
      setPendingSwitchPlan(plan);
      setConfirmSwitchOpen(true);
      return;
    }

    await proceedCheckout(plan);
  };

  const proceedCheckout = async (plan) => {
    if (!plan) return;

    try {
      setLoadingPlan(plan.name);
      let userId = user?.id;

      // If client-side user object is missing id (timing/cookie race), try server verify
      if (!userId) {
        try {
          const verifyResp = await axios.get(`${API_URL}/auth/verify`, {
            withCredentials: true,
          });
          userId = verifyResp.data?.user?.id;
        } catch (verifyErr) {
          console.warn(
            "Could not refresh user from server before checkout:",
            verifyErr?.message || verifyErr,
          );
        }
      }

      const event_id = await fireCheckoutIntent(plan, user);

      const payload = {
        planName: plan.name.toLowerCase(),
        price: plan.amount,
        event_id,
      };

      const resp = await axios.post(
        `${API_URL}/stripe/create-checkout-session`,
        payload,
        { withCredentials: true },
      );

      const data = resp.data || {};

      // Plan was updated in-place (no checkout needed — user already subscribed)
      if (data.planUpdated) {
        showSuccess(
          `Plan changed to ${data.plan ? data.plan.charAt(0).toUpperCase() + data.plan.slice(1) : "new plan"} successfully.`,
          "Plan Updated",
        );
        window.location.reload();
        return;
      }

      if (data.url) {
        window.location.href = data.url;
        return;
      }

      if (data.sessionId) {
        if (window.Stripe && process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
          const stripe = window.Stripe(
            process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
          );
          await stripe.redirectToCheckout({ sessionId: data.sessionId });
          return;
        }
      }

      const status = resp?.status;
      const serverMsg = resp?.data?.message || resp?.data?.error;
      showError(
        `Unable to start checkout${status ? ` (HTTP ${status})` : ""}: ${serverMsg || "Please try again or contact support."}`,
        "Checkout Error",
      );
    } catch (err) {
      console.error("Checkout error", err);
      const status = err.response?.status;
      const serverMsg =
        err.response?.data?.message || err.response?.data?.error;
      showError(
        `Unable to start checkout${status ? ` (HTTP ${status})` : ""}: ${serverMsg || err.message}`,
        "Checkout Error",
      );
    } finally {
      setLoadingPlan(null);
    }
  };

  const confirmTrialPlanSwitch = async () => {
    const selectedPlan = pendingSwitchPlan;
    setConfirmSwitchOpen(false);
    setPendingSwitchPlan(null);
    await proceedCheckout(selectedPlan);
  };

  // Coming from Settings > Billing "Switch to X" — that button now carries the
  // intended plan here instead of just dropping the user on this page, so the
  // switch starts automatically once subscription status has loaded (no second click).
  useEffect(() => {
    if (autoSwitchHandledRef.current) return;
    const requestedPlanName = location.state?.switchToPlan;
    if (!requestedPlanName || !subscription) return;

    const plan = plans.find(
      (p) => p.name.toLowerCase() === requestedPlanName.toLowerCase(),
    );
    if (!plan) return;

    autoSwitchHandledRef.current = true;
    navigate(location.pathname, { replace: true, state: {} });
    startCheckout(plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription, location.state]);

  // Coming from Settings > Billing "Cancel Free Trial" — give the user a
  // last chance to skip the trial and pay now instead of just canceling.
  useEffect(() => {
    if (promptCancelTrialHandledRef.current) return;
    if (!location.state?.promptCancelTrial || !subscription) return;
    if (subscription.subscriptionStatus !== "trialing") return;

    promptCancelTrialHandledRef.current = true;
    navigate(location.pathname, { replace: true, state: {} });
    setConfirmCancelTrialOpen(true);
  }, [subscription, location.state, location.pathname, navigate]);

  const executeCancelSubscription = async () => {
    try {
      setCanceling(true);
      await axios.post(
        `${API_URL}/stripe/cancel-subscription`,
        {},
        { withCredentials: true },
      );
      showSuccess(
        "Your subscription has been set to cancel at the end of the current period.",
        "Subscription Canceled",
      );
      fetchSubscriptionStatus();
    } catch (err) {
      console.error("Cancellation error", err);
      const serverMsg =
        err.response?.data?.message || err.response?.data?.error;
      showError(
        `Failed to cancel subscription: ${serverMsg || err.message}`,
        "Cancellation Error",
      );
    } finally {
      setCanceling(false);
      setConfirmCancelOpen(false);
    }
  };

  const executeUpgradePlan = async () => {
    try {
      setUpgrading(true);
      await axios.post(
        `${API_URL}/stripe/end-trial`,
        {},
        { withCredentials: true },
      );
      showInfo(
        "Activating your plan. Please wait a moment...",
        "Upgrade Started",
      );

      const pollAuth = async (retries = 10) => {
        if (retries <= 0) {
          showWarning(
            "Plan upgrade taking longer than expected. Please refresh the page.",
            "Upgrade Taking Longer",
          );
          setUpgrading(false);
          setConfirmUpgradeOpen(false);
          return;
        }

        try {
          const updatedUser = await verifyToken();

          if (updatedUser && updatedUser.subscriptionStatus === "active") {
            await fetchSubscriptionStatus();
            showSuccess(
              "Your plan has been upgraded! You now have full access.",
              "Upgrade Successful",
            );
            setUpgrading(false);
            setConfirmUpgradeOpen(false);
            return;
          }
        } catch (e) {
          console.error("Polling error", e);
        }

        setTimeout(() => pollAuth(retries - 1), 1000);
      };

      pollAuth();
    } catch (err) {
      console.error("Error ending trial:", err);
      const serverMsg =
        err.response?.data?.message || err.response?.data?.error;
      showError(`Upgrade Error: ${serverMsg || err.message}`, "Upgrade Error");
      setUpgrading(false);
      setConfirmUpgradeOpen(false);
    }
  };

  const handleUpgradePlan = () => {
    setConfirmUpgradeOpen(true);
  };

  const isCurrentPlan = (planName) => {
    const status = subscription?.subscriptionStatus;
    const onThisPlan =
      subscription?.subscriptionPlan?.toLowerCase() === planName.toLowerCase();
    return (
      onThisPlan &&
      (status === "active" ||
        status === "trialing" ||
        status === "past_due" ||
        status === "unpaid")
    );
  };

  const subscriptionPaymentBlocked =
    subscription?.paymentAccessBlocked ??
    shouldBlockSubscriptionAccess(subscription);
  const subscriptionInGrace =
    subscription?.isInPaymentGrace ?? isInPaymentGracePeriod(subscription);
  const subscriptionDelinquent =
    subscription?.subscriptionStatus === "past_due" ||
    subscription?.subscriptionStatus === "unpaid";

  const isCanceled = (planName) => {
    return (
      subscription?.subscriptionPlan?.toLowerCase() ===
        planName.toLowerCase() &&
      subscription?.subscriptionStatus === "canceled"
    );
  };

  return (
    <div className={`pricing-wrapper ${darkMode ? "dark" : ""}`}>
      <SEO title="Pricing" path="/pricing" description="Choose your LawFusion plan. Essentials at $19.95/month or Professional at $29.95/month. 7-day free trial included." />
      <div className="pricing-page">
        <Header />
        <div className="pricing-container">
          <h1 className="pricing-title">Pricing Plans</h1>
          <p className="pricing-subtitle pricing-subtitle-strong">
            Research, challenge, and strengthen your arguments before filing.
          </p>
          <p className="pricing-subtitle pricing-subtitle-strong">
            Get multiple top-tier AI models for about the cost of one basic AI
            subscription.
          </p>
          <p className="pricing-subtitle pricing-subtitle-bottom">
            Start with a 7-day free trial. Cancel anytime.
          </p>

          <div className="pricing-featured-callout">
            <span className="pricing-callout-icon" aria-hidden="true">
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M16 3.5a3.5 3.5 0 0 1 0 7" />
                <path d="M14.5 21v-2a3.5 3.5 0 0 0-3.5-3.5H6A3.5 3.5 0 0 0 2.5 19v2" />
                <circle cx="8.25" cy="7" r="3.5" />
                <path
                  d="m19.5 12 .7 1.5 1.6.2-1.2 1.1.3 1.6-1.4-.8-1.4.8.3-1.6-1.2-1.1 1.6-.2z"
                  fill="currentColor"
                  strokeWidth="0.5"
                />
              </svg>
            </span>
            <div>
              <p className="pricing-callout-headline">
                Featured Directory Placement — Included with Every Paid Plan
              </p>
              <p className="pricing-callout-body">
                Get a profile image, highlighted listing, and priority placement
                within your practice-area search results — helping increase
                visibility with referral sources and potential clients.
              </p>
            </div>
          </div>

          <div className="pricing-grid">
            {plans.map((plan, index) => {
              const currentPlan = isCurrentPlan(plan.name);
              const canceled = isCanceled(plan.name);
              const hasAnySubscription = subscription?.subscriptionStatus === "active" || subscription?.subscriptionStatus === "trialing";
              const isHighlighted = hasAnySubscription ? currentPlan : plan.highlight;

              return (
                <div
                  key={plan.name}
                  className={`pricing-card ${isHighlighted ? "pricing-card-highlight" : ""} ${currentPlan ? "pricing-card-current" : ""}`}
                >
                  {currentPlan && (
                    <div className="current-plan-badge">Your Current Plan</div>
                  )}
                  <h2 className="pricing-plan-name">{plan.name}</h2>
                  <p
                    className={`pricing-price ${isHighlighted ? "pricing-price-highlight" : ""}`}
                  >
                    {plan.price}
                  </p>

                  <ul className="pricing-features">
                    {plan.features.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>

                  {/* Collapsible explainer */}
                  <div className="pricing-explainer">
                    <button
                      onClick={() => toggleExpand(index)}
                      className="pricing-explainer-btn"
                    >
                      <span>What is an AI Action?</span>
                      <span className="pricing-arrow">
                        {expanded[index] ? "▲" : "▼"}
                      </span>
                    </button>
                    <AnimatePresence>
                      {expanded[index] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          style={{ overflow: "hidden" }}
                        >
                          <div className="pricing-explainer-content">
                            <p className="pricing-explainer-text">
                              Every LawFusion process is measured in{" "}
                              <strong>AI Actions</strong>. Each Action
                              represents one operation within your workspace.
                            </p>
                            <p className="pricing-explainer-text">
                              For example:
                            </p>
                            <ul className="pricing-examples">
                              <li>
                                <strong>1 Action</strong> — Ask one AI (e.g.,
                                “Summarize this statute”).
                              </li>
                              <li>
                                <strong>4 Actions</strong> — Compare answers
                                from 4 AIs on the same question.
                              </li>
                              <li>
                                <strong>1 Additional Action</strong> — Run a
                                final synthesis to merge and fact-check those
                                answers.
                              </li>
                            </ul>
                            <p className="pricing-explainer-text">
                              You can easily monitor your usage through the{" "}
                              <strong>AI Action Counter</strong> displayed on
                              the left side panel. It updates in real time to
                              show how many Actions have been used and how many
                              remain in your plan.
                            </p>
                            {rolloverLine(plan.actions)}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {currentPlan ? (
                    <div
                      className="current-plan-section"
                      style={{ marginTop: "auto" }}
                    >
                      <div
                        className="pricing-trial-callout"
                        style={{
                          border: "1px solid #ddd",
                          padding: "1rem",
                          borderRadius: "0.75rem",
                          background: "rgba(0,0,0,0.02)",
                        }}
                      >
                        <p
                          className="pricing-trial-title"
                          style={{
                            fontSize: "1rem",
                            color: subscriptionDelinquent
                              ? subscriptionPaymentBlocked
                                ? "#b91c1c"
                                : "#b45309"
                              : "#28a745",
                          }}
                        >
                          {subscription.subscriptionStatus === "trialing"
                            ? "✓ You are in Free Trial"
                            : subscriptionDelinquent
                              ? subscriptionPaymentBlocked
                                ? "⚠ Payment failed — access suspended"
                                : "⚠ Payment past due — grace period"
                              : "✓ Active Subscription"}
                        </p>
                        <p className="pricing-trial-subtitle">
                          {subscription.subscriptionStatus === "trialing"
                            ? `Trial ends: ${new Date(subscription.trialDetails?.trialEnd || subscription.trialEnd || subscription.subscriptionEndDate).toLocaleDateString()}`
                            : subscriptionDelinquent
                              ? subscriptionPaymentBlocked
                                ? "Update your payment method in Settings to restore access."
                                : "Update billing before grace ends to avoid losing access."
                              : subscription.cancelAtPeriodEnd
                                ? `Cancels on: ${new Date(subscription.currentPeriodEnd || subscription.subscriptionEndDate).toLocaleDateString()}`
                                : `Next billing: ${new Date(subscription.currentPeriodEnd || subscription.subscriptionEndDate).toLocaleDateString()}`}
                        </p>
                      </div>

                      {subscription.subscriptionStatus === "trialing" && (
                        <button
                          className="pricing-cta-btn"
                          onClick={handleUpgradePlan}
                          disabled={upgrading || canceling}
                          style={{
                            marginTop: "1rem",
                            padding: "0.75rem 0",
                            backgroundColor: "#4f46e5",
                            color: "white",
                            border: "none",
                            opacity: upgrading ? 0.6 : 1,
                            cursor: upgrading ? "not-allowed" : "pointer",
                          }}
                        >
                          {upgrading
                            ? "Activating Paid Plan..."
                            : "Skip Trial & Upgrade to Full Access"}
                        </button>
                      )}

                      {/* Cancel subscription is managed in Settings > Billing */}
                      {subscription.subscriptionStatus !== "trialing" && (
                        <button
                          className="pricing-cta-btn pricing-cta-btn-outline"
                          onClick={() => navigate("/settings?tab=billing")}
                          style={{ marginTop: "0.75rem" }}
                        >
                          Billing & Subscription
                        </button>
                      )}
                    </div>
                  ) : (
                    <div
                      className="pricing-buy-section"
                      style={{
                        marginTop: "auto",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {!canceled && (
                        <div className="pricing-trial-callout">
                          {hasAnySubscription ? (
                            <p className="pricing-trial-title">
                              Switch to {plan.name}
                            </p>
                          ) : !user?.hasUsedTrial ? (
                            null
                          ) : (
                            <p className="pricing-trial-title">
                              Monthly Subscription
                            </p>
                          )}
                        </div>
                      )}

                      {canceled && (
                        <div className="pricing-trial-callout">
                          <p
                            className="pricing-trial-title"
                            style={{ color: "#ff4d4d" }}
                          >
                            Plan Canceled
                          </p>
                          <p className="pricing-trial-subtitle">
                            Access until{" "}
                            {new Date(
                              subscription.subscriptionEndDate,
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      )}

                      <button
                        className={`pricing-cta-btn ${isHighlighted ? "pricing-cta-btn-highlight" : "pricing-cta-btn-secondary"}`}
                        onClick={() => startCheckout(plan)}
                        disabled={loadingPlan === plan.name || canceled}
                        style={{
                          opacity:
                            canceled || loadingPlan === plan.name ? 0.6 : 1,
                          cursor:
                            canceled || loadingPlan === plan.name
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        {loadingPlan === plan.name
                          ? hasAnySubscription
                            ? "Updating Plan..."
                            : "Starting..."
                          : canceled
                            ? "Access until period ends"
                            : hasAnySubscription
                              ? `Switch to ${plan.name}`
                              : !user?.hasUsedTrial
                                ? "Start Your 7-Day Free Trial"
                                : `Subscribe to ${plan.name}`}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="pricing-footer-note">
            <p>
              • This isn't a chatbot. It's your dedicated legal research
              associate, available 24/7.
            </p>
          </div>

          <p className="pricing-footer-disclaimer">
            All prices in USD. Secure payments via Stripe.
          </p>
        </div>
      </div>
      <Footer />

      <ConfirmModal
        isOpen={confirmCancelOpen}
        title="Cancel Subscription"
        message="Are you sure you want to cancel your subscription? You will still retain access to your plan and all unlocked features until the end of your current billing period."
        confirmText="Yes, Cancel Plan"
        cancelText="Keep My Plan"
        onConfirm={executeCancelSubscription}
        onClose={() => setConfirmCancelOpen(false)}
        isDangerous={true}
        isLoading={canceling}
        darkMode={false} // Match if Pricing is light/dark mode by default
      />

      <ConfirmModal
        isOpen={confirmSwitchOpen}
        title="Confirm Trial Plan Switch"
        message={`You are currently on a free trial. Do you want to switch to ${pendingSwitchPlan?.name || "the selected plan"} now?`}
        confirmText="Yes, Switch Plan"
        cancelText="No, Keep Current Trial Plan"
        onConfirm={confirmTrialPlanSwitch}
        onClose={() => {
          setConfirmSwitchOpen(false);
          setPendingSwitchPlan(null);
        }}
        isDangerous={false}
        isLoading={!!loadingPlan}
        darkMode={darkMode}
      />

      <ConfirmModal
        isOpen={confirmUpgradeOpen}
        title="Skip Trial & Upgrade"
        message="This will immediately end your free trial and charge your card for the full subscription price. Do you want to continue?"
        confirmText="Yes, Upgrade Now"
        cancelText="No, Stay on Trial"
        onConfirm={executeUpgradePlan}
        onClose={() => setConfirmUpgradeOpen(false)}
        isDangerous={false}
        isLoading={upgrading}
        darkMode={darkMode}
      />

      <AnimatePresence>
        {confirmCancelTrialOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000000,
              padding: "1rem",
              backdropFilter: "blur(4px)",
            }}
            onClick={() => setConfirmCancelTrialOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                borderRadius: "1rem",
                padding: "2rem",
                maxWidth: "440px",
                width: "100%",
                boxShadow:
                  "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                border: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
                position: "relative",
              }}
            >
              <button
                onClick={() => setConfirmCancelTrialOpen(false)}
                style={{
                  position: "absolute",
                  top: "1rem",
                  right: "1rem",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: darkMode ? "#94a3b8" : "#64748b",
                  padding: "0.25rem",
                  display: "flex",
                }}
              >
                <X size={20} />
              </button>

              <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    backgroundColor: darkMode
                      ? "rgba(99, 102, 241, 0.1)"
                      : "rgba(79, 70, 229, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1rem",
                    color: darkMode ? "#818cf8" : "#4f46e5",
                  }}
                >
                  <AlertCircle size={24} />
                </div>
                <h3
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "600",
                    color: darkMode ? "#f8fafc" : "#0f172a",
                    marginBottom: "0.75rem",
                  }}
                >
                  Cancel Your Free Trial?
                </h3>
                <p
                  style={{
                    fontSize: "0.95rem",
                    color: darkMode ? "#94a3b8" : "#64748b",
                    lineHeight: "1.5",
                  }}
                >
                  You're still in your 7-day free trial. You can cancel now,
                  or skip the rest of the trial and unlock full access
                  immediately by paying today.
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <button
                  onClick={() => {
                    setConfirmCancelTrialOpen(false);
                    setConfirmUpgradeOpen(true);
                  }}
                  style={{
                    padding: "0.75rem",
                    backgroundColor: "#4f46e5",
                    color: "white",
                    borderRadius: "0.5rem",
                    border: "none",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  No, Upgrade & Pay Now
                </button>
                <button
                  onClick={() => {
                    setConfirmCancelTrialOpen(false);
                    setConfirmCancelOpen(true);
                  }}
                  style={{
                    padding: "0.75rem",
                    backgroundColor: "transparent",
                    color: "#ef4444",
                    borderRadius: "0.5rem",
                    border: "1px solid #ef4444",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  Yes, Cancel My Trial
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
